import {
  Directive,
  ElementRef,
  HostBinding,
  HostListener,
  NgModule,
  OnDestroy,
  OnInit,
  Optional,
  Renderer2,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subject, takeUntil, tap } from 'rxjs';

/**
 * InputText directive is an extension to standard input element with theming.
 * @group Components
 */
@Directive({
  selector: '[systemInputText]',
  host: {
    class:
      'bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-system-400 focus:border-system-400 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-system-primary dark:focus:border-system-primary',
  },
})
export class InputTextDirective implements OnInit, OnDestroy {
  @HostBinding('class') className =
    'bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-system-600 focus:border-system-600 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-system-600 dark:focus:border-system-600';
  private destroy$ = new Subject<void>();
  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    @Optional() private control: NgControl
  ) {}

  ngOnInit(): void {
    if (this.control) {
      this.control.statusChanges
        ?.pipe(
          takeUntil(this.destroy$),
          tap(() => {
            this.applyStyles();
          })
        )
        .subscribe();

      this.control.valueChanges
        ?.pipe(
          takeUntil(this.destroy$),
          tap(() => this.applyStyles()) // Apply styles on value change
        )
        .subscribe();
    }
  }

  @HostListener('blur') onBlur() {
    this.applyStyles();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyStyles() {
    const errorClasses =
      'bg-red-50 border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500';
    const isInvalid = this.control ? this.control.invalid && (this.control.dirty || this.control.touched) : true;
    if (isInvalid) {
      this.addClasses(errorClasses);
    } else {
      this.removeClasses(errorClasses);
    }
  }

  private addClasses(classes: string) {
    const classList = classes.split(' ');
    classList.forEach(className => {
      if (!this.el.nativeElement.classList.contains(className)) {
        this.renderer.addClass(this.el.nativeElement, className);
      }
    });
  }

  private removeClasses(classes: string) {
    const classList = classes.split(' ');
    classList.forEach(className => {
      if (this.el.nativeElement.classList.contains(className)) {
        this.renderer.removeClass(this.el.nativeElement, className);
      }
    });
  }
}

@NgModule({
  declarations: [InputTextDirective],
  exports: [InputTextDirective],
})
export class SystemInputTextModule {}
